import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Info, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PayoutData {
  hourly_rate: number; // dollars
  effective_payout_rate: number; // dollars/hr
  total_sessions_this_month: number;
  total_earnings_this_month: number; // dollars
  last_month_effective_rate: number; // dollars/hr
}

interface MentorPayoutTransparencyProps {
  mentorId: string;
}

export function MentorPayoutTransparency({ mentorId }: MentorPayoutTransparencyProps) {
  const [payoutData, setPayoutData] = useState<PayoutData | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchPayoutData();
  }, [mentorId]);

  const computeEffectiveRate = (
    sessions: Array<{ mentor_payout_amount: number | null; duration_minutes: number | null }>,
    fallbackHourlyDollars: number
  ): number => {
    if (!sessions || sessions.length === 0) return fallbackHourlyDollars;
    let totalPayout = 0;
    let totalHours = 0;
    for (const s of sessions) {
      const payout = Number(s.mentor_payout_amount || 0);
      const hours = (s.duration_minutes || 0) / 60;
      if (hours > 0) {
        totalPayout += payout;
        totalHours += hours;
      }
    }
    return totalHours > 0 ? totalPayout / totalHours : fallbackHourlyDollars;
  };

  const fetchPayoutData = async () => {
    try {
      setLoading(true);

      const { data: mentor, error: mentorError } = await supabase
        .from('mentors')
        .select('hourly_rate')
        .eq('id', mentorId)
        .single();

      if (mentorError) throw mentorError;

      const hourlyDollars = mentor.hourly_rate / 100;
      const expectedEffective = hourlyDollars * 0.925;

      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { data: sessions, error: sessionsError } = await supabase
        .from('sessions')
        .select('mentor_payout_amount, duration_minutes, completed_at')
        .eq('mentor_id', mentorId)
        .eq('status', 'completed')
        .gte('completed_at', startOfMonth.toISOString());

      if (sessionsError) throw sessionsError;

      const totalSessions = sessions?.length || 0;
      const totalEarnings = sessions?.reduce(
        (sum, s) => sum + Number(s.mentor_payout_amount || 0),
        0
      ) || 0;
      const effectiveRate = computeEffectiveRate(sessions || [], expectedEffective);

      const lastMonthStart = new Date(startOfMonth);
      lastMonthStart.setMonth(lastMonthStart.getMonth() - 1);
      const lastMonthEnd = new Date(startOfMonth);

      const { data: lastMonthSessions } = await supabase
        .from('sessions')
        .select('mentor_payout_amount, duration_minutes')
        .eq('mentor_id', mentorId)
        .eq('status', 'completed')
        .gte('completed_at', lastMonthStart.toISOString())
        .lt('completed_at', lastMonthEnd.toISOString());

      const lastMonthEffective = computeEffectiveRate(lastMonthSessions || [], expectedEffective);

      setPayoutData({
        hourly_rate: hourlyDollars,
        effective_payout_rate: effectiveRate,
        total_sessions_this_month: totalSessions,
        total_earnings_this_month: totalEarnings,
        last_month_effective_rate: lastMonthEffective,
      });
    } catch (error: any) {
      console.error('Error fetching payout data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load payout information',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = () => {
    if (!payoutData) return <Minus className="w-4 h-4" />;
    if (payoutData.effective_payout_rate > payoutData.last_month_effective_rate)
      return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (payoutData.effective_payout_rate < payoutData.last_month_effective_rate)
      return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5" />
            Payout Rate Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!payoutData) return null;

  const expectedPayout = payoutData.hourly_rate * 0.925;
  const platformFeePerHour = payoutData.hourly_rate * 0.075;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="w-5 h-5" />
          Payout Rate Transparency
        </CardTitle>
        <CardDescription>
          You earn 92.5% of your listed hourly rate. The platform fee is always 7.5%.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Your Listed Rate</label>
            <div className="text-2xl font-bold">${payoutData.hourly_rate.toFixed(2)}/hour</div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Your Payout Rate (92.5%)</label>
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold text-green-600">
                ${expectedPayout.toFixed(2)}/hour
              </div>
              {getTrendIcon()}
            </div>
            <div className="text-xs text-muted-foreground">
              Platform fee: ${platformFeePerHour.toFixed(2)}/hour (7.5%)
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold">{payoutData.total_sessions_this_month}</div>
            <div className="text-sm text-muted-foreground">Sessions This Month</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">${payoutData.total_earnings_this_month.toFixed(2)}</div>
            <div className="text-sm text-muted-foreground">Total Earnings This Month</div>
          </div>
        </div>

        {payoutData.total_sessions_this_month > 0 && (
          <div className="bg-muted/50 p-4 rounded-lg text-sm">
            <span className="font-medium">Actual effective rate this month: </span>
            ${payoutData.effective_payout_rate.toFixed(2)}/hour
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">How Payout Rates Work</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• You earn 92.5% of your listed hourly rate on every session</li>
            <li>• The platform fee is a flat 7.5% — no exceptions</li>
            <li>• Students always redeem credits at the standard $1.00/credit rate</li>
            <li>• Your payout is calculated from your listed rate and session duration</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
